import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiEndpoints, ApiService } from '@nano/core/api';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageCount, MessageThread, MessageThreadsResponse } from './message';

@Injectable({ providedIn: 'root' })
export class MessageService {
  readonly messageCount$: Observable<MessageCount>;
  readonly messageThreadRemoved$: Observable<string>;
  readonly messageThreads$: Observable<MessageThread[]>;
  readonly unreadMessageThreads$: Observable<MessageThread[]>;

  private messageCountSubject: Subject<MessageCount>;
  private messageThreadRemovedSubject: Subject<string>;
  private messageThreadsSubject: Subject<MessageThread[]>;
  private unreadMessageThreadsSubject: ReplaySubject<MessageThread[]>;
  private unreadMessageThreadsLimit: number;
  private messageThreadsLimit = 1000;

  private config = inject(CONFIG);

  constructor(
    private apiService: ApiService,
    private errorService: ErrorService
  ) {
    this.messageCountSubject = new Subject<MessageCount>();
    this.messageCount$ = this.messageCountSubject.asObservable();

    this.messageThreadRemovedSubject = new Subject();
    this.messageThreadRemoved$ = this.messageThreadRemovedSubject.asObservable();

    this.messageThreadsSubject = new ReplaySubject<MessageThread[]>(1);
    this.messageThreads$ = this.messageThreadsSubject.asObservable();

    this.unreadMessageThreadsSubject = new ReplaySubject<MessageThread[]>(1);
    this.unreadMessageThreads$ = this.unreadMessageThreadsSubject.asObservable();

    this.unreadMessageThreadsLimit = this.config.messagesEnvelopeLimit;
  }

  loadUnreadMessageThreads(): void {
    this.loadMessageThreads(true, this.unreadMessageThreadsLimit).subscribe({
      next: (messageThreads) => this.unreadMessageThreadsSubject.next(messageThreads),
      error: (error: unknown) => this.errorService.handleRequestError(error)
    });
  }

  loadAllMessageThreads(): void {
    this.loadMessageThreads(false, this.messageThreadsLimit).subscribe({
      next: (messageThreads) => {
        this.messageThreadsSubject.next(messageThreads);
        this.unreadMessageThreadsSubject.next(
          messageThreads.filter((messageThread: MessageThread) => !messageThread.isRead).slice(0, this.unreadMessageThreadsLimit)
        );
      },
      error: (error: unknown) => this.errorService.handleRequestError(error)
    });
  }

  private loadMessageThreads(unreadOnly: boolean, limit: number): Observable<MessageThread[]> {
    const params = new HttpParams().set('unreadOnly', unreadOnly.toString()).set('limit', limit.toString());
    return this.apiService.get<MessageThreadsResponse>(ApiEndpoints.messageThreads(), params).pipe(
      map((response: MessageThreadsResponse) => {
        this.messageCountSubject.next(response.messageCount);
        return response.messageThreads;
      })
    );
  }
}
